/*
 * MultiSelect — Dropdown con selección múltiple via checkboxes
 * ─────────────────────────────────────────────────────────────────
 *
 * Props:
 *   label        string    Etiqueta sobre el input
 *   value        array     Array de valores seleccionados
 *   onChange     fn        Recibe el array actualizado de valores seleccionados
 *   options      array     [{ name: string, value: any }, ...]
 *   mapper       object    Mapeo de campos cuando options no usa { name, value }.
 *                          Formato: { name: 'campoNombre', value: 'campoValor' }
 *                          Ej: mapper={{ name: 'name', value: 'code' }}
 *   placeholder  string    Texto cuando no hay selección (default: "Seleccionar...")
 *   icon         string    Ícono a la izquierda (opcional)
 *   searchable   bool      Muestra campo de búsqueda (default: true)
 *   disabled     bool      Deshabilita el componente
 *   error        bool      Activa estilos de error
 *   errorMessage string    Texto del mensaje de error
 *
 * Uso básico:
 *   const [sel, setSel] = useState([]);
 *
 *   <MultiSelect
 *       label="Países"
 *       value={sel}
 *       onChange={setSel}
 *       options={[
 *           { name: "México", value: "mx" },
 *           ...
 *       ]}
 *   />
 *
 * Con mapper:
 *
 *   <MultiSelect
 *       label="Clientes"
 *       value={sel}
 *       onChange={setSel}
 *       options={clientes}
 *       mapper={{ name: 'name', value: 'code' }}
 *   />
 *
 * ─────────────────────────────────────────────────────────────────
 */

import React, { useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import Text from "../Text/Text";
import Checkbox from "../Checkbox/Checkbox";
import "./MultiSelect.scss";

const MultiSelect = ({
    label,
    style,
    value = [],
    onChange,
    options = [],
    mapper = { name: "name", value: "value" },
    placeholder = "Seleccionar...",
    icon,
    searchable = true,
    disabled = false,
    error = false,
    errorMessage = "Campo requerido",
}) => {

    const [open, setOpen] = useState(false);
    const [closing, setClosing] = useState(false);
    const [search, setSearch] = useState("");

    const ref = useRef(null);
    const searchRef = useRef(null);
    const isOpenRef = useRef(false);
    const dropdownRef = useRef(null);

    const [dropdownPos, setDropdownPos] = useState({
        top: 0,
        left: 0,
        width: 0
    });

    // ============================================================
    // Posición del dropdown
    // ============================================================

    const calcPos = () => {
        if (!ref.current) return;

        const rect = ref.current.getBoundingClientRect();

        setDropdownPos({
            top: rect.bottom - 14,
            left: rect.left,
            width: rect.width
        });
    };

    // ============================================================
    // Normalizar opciones
    // ============================================================

    const nameKey = mapper?.name ?? "name";
    const valueKey = mapper?.value ?? "value";

    const normalizedOptions = options.map((o) => ({
        ...o,
        name: String(
            o[nameKey] ?? o.name ?? ""
        ).trim(),

        value: o[valueKey] ?? o.value ?? o.code,
    }));

    // Asegurarnos de que siempre sea un array
    const selected = Array.isArray(value) ? value : [];

    // ============================================================
    // Obtener el valor de un elemento seleccionado
    // ============================================================

    const getSelectedValue = (item) => {
        if (item == null) return undefined;

        if (typeof item === "object") {
            return item[valueKey] ?? item.value ?? item.code;
        }

        return item;
    };

    // ============================================================
    // Saber si una opción está seleccionada
    // ============================================================

    const isSelected = (opt) => {
        return selected.some((s) => {
            const selectedValue = getSelectedValue(s);

            return selectedValue === opt.value;
        });
    };

    // ============================================================
    // Filtrar opciones
    // ============================================================

    const filtered = search.trim()
        ? normalizedOptions.filter((o) =>
            String(o.name)
                .toLowerCase()
                .includes(search.toLowerCase())
        )
        : normalizedOptions;

    // ============================================================
    // Estado del "Seleccionar todo"
    // ============================================================

    const allFilteredSelected =
        filtered.length > 0 &&
        filtered.every((o) => isSelected(o));

    const someFilteredSelected =
        filtered.some((o) => isSelected(o)) &&
        !allFilteredSelected;

    // ============================================================
    // Texto mostrado en el input
    // ============================================================

    const displayText = () => {

        if (selected.length === 0) {
            return null;
        }

        if (selected.length <= 1) {
            return selected
                .map((s) => {
                    if (typeof s === "object") {
                        return s[nameKey] ?? s.name;
                    }

                    const option = normalizedOptions.find(
                        (o) => o.value === s
                    );

                    return option?.name ?? s;
                })
                .join(", ");
        }

        return `${selected.length} seleccionados`;
    };

    // ============================================================
    // Cerrar al hacer click fuera
    // ============================================================

    useEffect(() => {

        const handleOutside = (e) => {

            if (
                isOpenRef.current &&
                ref.current &&
                !ref.current.contains(e.target) &&
                !(
                    dropdownRef.current &&
                    dropdownRef.current.contains(e.target)
                )
            ) {
                isOpenRef.current = false;
                setOpen(false);
                setClosing(true);
            }
        };

        document.addEventListener("mousedown", handleOutside);

        return () => {
            document.removeEventListener(
                "mousedown",
                handleOutside
            );
        };

    }, []);

    // ============================================================
    // Actualizar posición
    // ============================================================

    useEffect(() => {

        if (!open) return;

        const update = () => {
            calcPos();
        };

        window.addEventListener("scroll", update, true);
        window.addEventListener("resize", update);

        return () => {
            window.removeEventListener(
                "scroll",
                update,
                true
            );

            window.removeEventListener(
                "resize",
                update
            );
        };

    }, [open]);

    // ============================================================
    // Foco en búsqueda
    // ============================================================

    useEffect(() => {

        if (
            open &&
            searchable &&
            searchRef.current
        ) {
            searchRef.current.focus();
        }

    }, [open, searchable]);

    // ============================================================
    // Abrir
    // ============================================================

    const handleOpen = () => {

        if (
            disabled ||
            isOpenRef.current ||
            closing
        ) {
            return;
        }

        calcPos();

        isOpenRef.current = true;

        setOpen(true);
        setSearch("");
    };

    // ============================================================
    // Cerrar
    // ============================================================

    const handleClose = () => {

        if (!isOpenRef.current) {
            return;
        }

        isOpenRef.current = false;

        setOpen(false);
        setClosing(true);
    };

    // ============================================================
    // Fin de animación
    // ============================================================

    const handleAnimEnd = () => {

        if (closing) {
            setClosing(false);
        }

    };

    // ============================================================
    // Seleccionar / deseleccionar opción
    // ============================================================

    const handleToggle = (opt) => {

        const alreadySelected = isSelected(opt);

        let next;

        if (alreadySelected) {

            // ELIMINAR
            next = selected.filter((s) => {

                const selectedValue =
                    getSelectedValue(s);

                return selectedValue !== opt.value;

            });

        } else {

            // AGREGAR
            next = [
                ...selected,
                opt
            ];

        }

        onChange?.(next);
    };

    // ============================================================
    // Seleccionar / deseleccionar todos
    // ============================================================

    const handleToggleAll = () => {

        if (allFilteredSelected) {

            // Todos los filtrados están seleccionados
            // → quitarlos

            const next = selected.filter((s) => {

                const selectedValue =
                    getSelectedValue(s);

                return !filtered.some(
                    (o) => o.value === selectedValue
                );

            });

            onChange?.(next);

        } else {

            // Agregar los que todavía no están seleccionados

            const nuevos = filtered.filter(
                (opt) => !isSelected(opt)
            );

            onChange?.([
                ...selected,
                ...nuevos
            ]);
        }
    };

    // ============================================================
    // Limpiar todo
    // ============================================================

    const handleClear = (e) => {

        e.stopPropagation();

        onChange?.([]);
    };

    // ============================================================
    // Teclas
    // ============================================================

    const handleKeyDown = (e) => {

        if (e.key === "Escape") {

            e.preventDefault();

            handleClose();
        }
    };

    const text = displayText();

    // ============================================================
    // Render
    // ============================================================

    return (

        <div
            className={`
                input-field
                MultiSelect
                ${error ? "has-error" : ""}
                ${open ? "is-open" : ""}
                animate__animated
                animate__fadeInUp
            `}
            ref={ref}
            style={style}
        >

            {/* LABEL */}

            {label && (

                <Text
                    size="small"
                    color="var(--gray-dark-500)"
                    className="input-label"
                >
                    {label}
                </Text>

            )}

            {/* INPUT */}

            <div className="input-wrapper">

                {icon && (

                    <span
                        className="icon input-icon"
                        data-icon={icon}
                    />

                )}

                <button
                    type="button"
                    className={`
                        input-element
                        sel-trigger
                        ${icon ? "has-left-icon" : ""}
                        ${disabled ? "disabled" : ""}
                    `}
                    onClick={handleOpen}
                    onKeyDown={handleKeyDown}
                    disabled={disabled}
                    aria-haspopup="listbox"
                    aria-expanded={open}
                >

                    <span
                        className={`
                            sel-value
                            ${!text ? "sel-placeholder" : ""}
                        `}
                    >
                        {text ?? placeholder}
                    </span>

                </button>

                <span
                    className={`
                        icon
                        sel-icon-right
                        ${open ? "sel-icon-open" : ""}
                    `}
                    data-icon="expand_more"
                />

                {/* LIMPIAR */}

                {selected.length > 0 && !disabled && (

                    <button
                        className="preview icon"
                        data-icon="close"
                        type="button"
                        onClick={handleClear}
                    />

                )}

            </div>

            {/* ERROR */}

            {error && (

                <Text
                    className="input-error"
                    color="var(--red-600)"
                    size="small"
                >
                    {errorMessage}
                </Text>

            )}

            {/* DROPDOWN */}

            {(open || closing) && createPortal(

                <div
                    className="MultiSelect"
                    style={{
                        position: "fixed",
                        top: 0,
                        left: 0,
                        width: 0,
                        height: 0,
                        overflow: "visible",
                        zIndex: 9999,
                        pointerEvents: "none"
                    }}
                >

                    <div
                        ref={dropdownRef}
                        className={`
                            sel-dropdown
                            ${closing
                                ? "sel-closing"
                                : "sel-opening"
                            }
                        `}
                        style={{
                            top: `${dropdownPos.top}px`,
                            left: `${dropdownPos.left}px`,
                            minWidth: `${dropdownPos.width}px`,
                            pointerEvents: "all"
                        }}
                        onAnimationEnd={handleAnimEnd}
                        onMouseDown={(e) => {
                            if (e.target.tagName !== 'INPUT') e.preventDefault();
                        }}
                        role="listbox"
                        aria-multiselectable="true"
                    >

                        {/* BUSCADOR */}

                        {searchable && (

                            <div className="sel-search-wrap">

                                <span
                                    className="icon sel-search-icon"
                                    data-icon="search"
                                />

                                <input
                                    ref={searchRef}
                                    className="sel-search"
                                    type="text"
                                    placeholder="Buscar..."
                                    value={search}
                                    onChange={(e) =>
                                        setSearch(e.target.value)
                                    }
                                />

                                {search && (

                                    <button
                                        className="sel-search-clear icon"
                                        data-icon="close"
                                        type="button"
                                        onClick={() =>
                                            setSearch("")
                                        }
                                    />

                                )}

                            </div>

                        )}

                        {/* SELECCIONAR TODO */}

                        {filtered.length > 0 && (

                            <div className="msel-toggle-all">

                                <Checkbox
                                    checked={allFilteredSelected}
                                    indeterminate={
                                        someFilteredSelected
                                    }
                                    onChange={
                                        handleToggleAll
                                    }
                                    label={
                                        allFilteredSelected
                                            ? "Deseleccionar todo"
                                            : "Seleccionar todo"
                                    }
                                />

                            </div>

                        )}

                        {/* LISTA */}

                        <ul className="sel-list">

                            {filtered.length === 0 ? (

                                <li className="sel-empty">
                                    Sin resultados
                                </li>

                            ) : (

                                filtered.map((opt) => {

                                    const isChecked =
                                        isSelected(opt);

                                    return (

                                        <li
                                            key={opt.value}
                                            className={`
                                                sel-option
                                                msel-option
                                                ${isChecked
                                                    ? "selected"
                                                    : ""
                                                }
                                            `}
                                            role="option"
                                            aria-selected={
                                                isChecked
                                            }
                                        >

                                            <Checkbox
                                                checked={
                                                    isChecked
                                                }
                                                onChange={() =>
                                                    handleToggle(opt)
                                                }
                                                label={
                                                    opt.name
                                                }
                                            />

                                        </li>

                                    );

                                })

                            )}

                        </ul>

                    </div>

                </div>,

                document.body

            )}

        </div>
    );
};

export default MultiSelect;